import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { trackElementImpression } from './tracking';

const elementKey = 'data-glints-tracking-view-element-name';
export default function useTrackElementImpression() {
  const cache = useRef<Record<string, string | number>>({});
  const { pathname } = useLocation();

  useEffect(() => {
    cache.current = {};
  }, [pathname]);

  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.intersectionRatio >= 1) {
            const target = entry.target?.closest(`[${elementKey}]`);
            if (!(target instanceof HTMLElement)) {
              return;
            }
            const elementName = target.getAttribute(elementKey);
            const elementId = target.getAttribute(
              'data-glints-tracking-view-element-id'
            );
            if (!elementName) return;
            const isElementInCache = Boolean(
              elementId && cache.current[elementId]
            );
            if (!isElementInCache) {
              if (elementId) {
                cache.current[elementId] = elementName;
              }
              trackElementImpression(target);
            }
          }
        });
      },
      { threshold: 1 }
    );

    const observeElement = (element: Element) => {
      if (element instanceof HTMLElement) {
        intersectionObserver.observe(element);
      }
    };

    document
      .querySelectorAll<HTMLElement>(`[${elementKey}]`)
      .forEach(observeElement);

    const mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLElement) {
            if (node.hasAttribute(elementKey)) {
              observeElement(node);
            }

            node.querySelectorAll(`[${elementKey}]`).forEach(observeElement);
          }
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
}
